import { Button } from "../../primitives/button";
import { SeverityBadge } from "../signals/SeverityBadge";
import { AlertCircle, CheckCircle, ExternalLink } from "lucide-react";
import type * as API from "@shared-core/data/api-types";
import { formatDistanceToNow } from "date-fns";
import { useState } from "react";
import { Link } from "react-router";
import { paths } from "@shared-core/routing/routes";
import { AlertDetailModal } from "./AlertDetailModal";
import { SnoozeAlertControl } from "./SnoozeAlertControl";

interface AlertItemProps {
  alert: API.Alert;
  onDismiss?: () => void;
  onConvertToTicket?: () => void;
  onViewTicket?: (ticketId: string) => void;
  onSnooze?: (durationPreset: string, reason?: string) => void;
}

export function AlertItem({
  alert,
  onDismiss,
  onConvertToTicket,
  onViewTicket,
  onSnooze,
}: AlertItemProps) {
  const timeAgo = formatDistanceToNow(new Date(alert.created_at), {
    addSuffix: true,
  });
  const isDismissed = alert.status === "dismissed";
  const isSnoozed = alert.status === "snoozed";
  const hasTicket = !!alert.converted_to_task_id;
  const customer = alert.customer ?? null;

  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <>
      <div
        onClick={() => setIsModalOpen(true)}
        className={`flex items-start gap-3 p-4 border border-gray-200 rounded-lg bg-card shadow-sm hover:bg-surface-hover transition-colors cursor-pointer ${isDismissed ? "opacity-60" : ""}`}
      >
        <AlertCircle className="size-5 text-destructive shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <h4 className={`font-medium ${isDismissed ? "line-through" : ""}`}>
              {alert.title}
            </h4>
            <SeverityBadge severity={alert.severity} />
          </div>
          <p className="text-sm text-muted-foreground mb-3">
            {alert.description}
          </p>
          {isSnoozed && alert.snooze_reason && (
            <p className="text-xs text-muted-foreground italic mb-3">
              Snoozed: {alert.snooze_reason}
            </p>
          )}
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">
                {customer && (
                  <>
                    <Link
                      to={paths.customerDetail({ customerId: customer.id })}
                      onClick={(e) => e.stopPropagation()}
                      className="hover:underline text-[color:var(--color-primary)]"
                    >
                      {customer.name}
                    </Link>
                    <span className="mx-1.5">•</span>
                  </>
                )}
                {timeAgo}
              </span>
            </div>
            {(hasTicket || alert.status === "open") && (
              <div
                className="flex items-center gap-2"
                onClick={(e) => e.stopPropagation()}
              >
                {hasTicket ? (
                  onViewTicket && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onViewTicket(alert.converted_to_task_id!)}
                    >
                      <ExternalLink className="size-3.5" />
                      View Ticket
                    </Button>
                  )
                ) : (
                  <>
                    {onConvertToTicket && (
                      <Button size="sm" onClick={onConvertToTicket}>
                        <CheckCircle className="size-3.5" />
                        Convert to Ticket
                      </Button>
                    )}
                    {onSnooze && (
                      <SnoozeAlertControl onSnooze={onSnooze} size="sm" />
                    )}
                    {onDismiss && (
                      <Button variant="ghost" size="sm" onClick={onDismiss}>
                        Dismiss
                      </Button>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {isModalOpen && (
        <AlertDetailModal
          alert={alert}
          onClose={() => setIsModalOpen(false)}
          onDismiss={
            onDismiss
              ? () => {
                  setIsModalOpen(false);
                  onDismiss();
                }
              : undefined
          }
          onConvertToTicket={
            onConvertToTicket
              ? () => {
                  setIsModalOpen(false);
                  onConvertToTicket();
                }
              : undefined
          }
          onViewTicket={
            onViewTicket
              ? (ticketId) => {
                  setIsModalOpen(false);
                  onViewTicket(ticketId);
                }
              : undefined
          }
          onSnooze={
            onSnooze
              ? (duration, reason) => {
                  setIsModalOpen(false);
                  onSnooze(duration, reason);
                }
              : undefined
          }
        />
      )}
    </>
  );
}
